import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle, CheckCircle, Clock, TrendingUp,
  Building2, Wrench, ShieldCheck, Loader2,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'
import { useAuthStore } from '@/store/auth'
import { apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'

interface GroupDashboard {
  properties: PropertyStats[]
  totals: {
    openWorkOrders: number
    overdueWorkOrders: number
    completedLast30Days: number
    overduePPMSchedules: number
    activeAlerts: number
  }
}

interface PropertyStats {
  property: { id: string; name: string; code: string }
  openWorkOrders: number
  overdueWorkOrders: number
  completedLast30Days: number
  overduePPMSchedules: number
  activeAlerts: number
}

interface PropertyKPIs {
  open: { byPriority: Record<string, number>; total: number }
  overdueCount: number
  completedLast30Days: number
  slaBreachesLast30Days: number
  avgCostPerWoGbp: number
}

const PRIORITY_COLORS_HEX: Record<string, string> = {
  EMERGENCY: '#DC2626', URGENT: '#EA580C', HIGH: '#D97706',
  STANDARD: '#2563EB', LOW: '#6B7280', PLANNED: '#7C3AED',
}

export function AnalyticsPage() {
  const { currentPropertyId, user } = useAuthStore()
  const pid = currentPropertyId
  const isDirector = ['GROUP_ADMIN', 'REGIONAL_DIRECTOR'].includes(user?.role ?? '')

  const { data: kpis, isLoading: kpiLoading } = useQuery({
    queryKey: ['wo-kpis', pid],
    queryFn: () => apiGet<PropertyKPIs>(`/properties/${pid}/work-orders/dashboard/kpis`),
    enabled: !!pid,
  })

  const { data: groupData } = useQuery({
    queryKey: ['group-dashboard'],
    queryFn: () => apiGet<GroupDashboard>('/analytics/group-dashboard'),
    enabled: isDirector,
  })

  const priorityData = kpis
    ? Object.entries(kpis.open.byPriority)
        .map(([name, value]) => ({ name, value }))
        .filter(d => d.value > 0)
    : []

  const propertyBarData = groupData?.properties.map(p => ({
    name: p.property.code,
    fullName: p.property.name,
    open: p.openWorkOrders,
    overdue: p.overdueWorkOrders,
    completed: p.completedLast30Days,
  })) ?? []

  const propertyPPMData = groupData?.properties.map(p => ({
    name: p.property.code,
    fullName: p.property.name,
    overduePPM: p.overduePPMSchedules,
    alerts: p.activeAlerts,
  })) ?? []

  if (kpiLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-800" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Analytics</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          {isDirector ? 'Group-wide performance overview' : 'Property performance overview'}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card py-4">
          <div className="flex items-center gap-2 mb-1">
            <Wrench className="w-4 h-4 text-navy-400" />
            <p className="text-xs text-gray-400">Open WOs</p>
          </div>
          <p className="text-2xl font-bold text-navy-800">{kpis?.open.total ?? 0}</p>
        </div>
        <div className="card py-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <p className="text-xs text-gray-400">Overdue</p>
          </div>
          <p className={cn('text-2xl font-bold', (kpis?.overdueCount ?? 0) > 0 ? 'text-red-600' : 'text-navy-800')}>
            {kpis?.overdueCount ?? 0}
          </p>
        </div>
        <div className="card py-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-xs text-gray-400">Completed (30d)</p>
          </div>
          <p className="text-2xl font-bold text-navy-800">{kpis?.completedLast30Days ?? 0}</p>
        </div>
        <div className="card py-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-400" />
            <p className="text-xs text-gray-400">SLA Breaches (30d)</p>
          </div>
          <p className={cn('text-2xl font-bold', (kpis?.slaBreachesLast30Days ?? 0) > 0 ? 'text-amber-600' : 'text-navy-800')}>
            {kpis?.slaBreachesLast30Days ?? 0}
          </p>
        </div>
        <div className="card py-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-gold-500" />
            <p className="text-xs text-gray-400">Avg Cost / WO</p>
          </div>
          <p className="text-2xl font-bold text-navy-800">
            {kpis?.avgCostPerWoGbp ? `£${Math.round(kpis.avgCostPerWoGbp)}` : '—'}
          </p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority pie */}
        <div className="card">
          <h2 className="text-sm font-semibold text-navy-800 mb-4">Open Work Orders by Priority</h2>
          {priorityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={priorityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name} (${value})`}
                  labelLine={false}
                >
                  {priorityData.map((entry) => (
                    <Cell key={entry.name} fill={PRIORITY_COLORS_HEX[entry.name] ?? '#6B7280'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">
              No open work orders
            </div>
          )}
        </div>

        {/* Group bar chart or SLA performance */}
        {isDirector && groupData ? (
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-4">Work Orders by Property</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={propertyBarData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'open' ? 'Open' : name === 'overdue' ? 'Overdue' : 'Completed (30d)',
                  ]}
                  labelFormatter={(label: string) => propertyBarData.find(p => p.name === label)?.fullName ?? label}
                />
                <Bar dataKey="open" fill="#1A2F4E" radius={[4, 4, 0, 0]} name="Open" />
                <Bar dataKey="overdue" fill="#DC2626" radius={[4, 4, 0, 0]} name="Overdue" />
                <Bar dataKey="completed" fill="#059669" radius={[4, 4, 0, 0]} name="Completed (30d)" />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-4">SLA Performance</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">SLA Compliance Rate</span>
                  <span className="font-medium text-navy-800">
                    {kpis && kpis.completedLast30Days > 0
                      ? `${Math.round(((kpis.completedLast30Days - kpis.slaBreachesLast30Days) / kpis.completedLast30Days) * 100)}%`
                      : '—'}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all"
                    style={{
                      width: kpis && kpis.completedLast30Days > 0
                        ? `${((kpis.completedLast30Days - kpis.slaBreachesLast30Days) / kpis.completedLast30Days) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {kpis ? kpis.completedLast30Days - kpis.slaBreachesLast30Days : 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Within SLA</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{kpis?.slaBreachesLast30Days ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-1">SLA Breached</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Group property table (directors only) */}
      {isDirector && groupData && (
        <>
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              Property Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-500">Property</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500">Open WOs</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500">Overdue</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500">Completed (30d)</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500">Overdue PPM</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500">Active Alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {groupData.properties.map((p) => (
                    <tr key={p.property.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-navy-800">{p.property.name}</p>
                        <p className="text-xs text-gray-400 font-mono">{p.property.code}</p>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-navy-800">{p.openWorkOrders}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn('font-medium', p.overdueWorkOrders > 0 ? 'text-red-600' : 'text-gray-400')}>
                          {p.overdueWorkOrders}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-green-600">{p.completedLast30Days}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn('font-medium', p.overduePPMSchedules > 0 ? 'text-amber-600' : 'text-gray-400')}>
                          {p.overduePPMSchedules}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn('font-medium', p.activeAlerts > 0 ? 'text-red-600' : 'text-gray-400')}>
                          {p.activeAlerts}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50/50 font-semibold">
                    <td className="px-4 py-3 text-navy-800">Totals</td>
                    <td className="px-4 py-3 text-center text-navy-800">{groupData.totals.openWorkOrders}</td>
                    <td className="px-4 py-3 text-center text-red-600">{groupData.totals.overdueWorkOrders}</td>
                    <td className="px-4 py-3 text-center text-green-600">{groupData.totals.completedLast30Days}</td>
                    <td className="px-4 py-3 text-center text-amber-600">{groupData.totals.overduePPMSchedules}</td>
                    <td className="px-4 py-3 text-center text-red-600">{groupData.totals.activeAlerts}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* PPM & Alerts chart */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-gray-400" />
              PPM Compliance & Alerts by Property
            </h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={propertyPPMData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  labelFormatter={(label: string) => propertyPPMData.find(p => p.name === label)?.fullName ?? label}
                />
                <Bar dataKey="overduePPM" fill="#D97706" radius={[4, 4, 0, 0]} name="Overdue PPM" />
                <Bar dataKey="alerts" fill="#DC2626" radius={[4, 4, 0, 0]} name="Active Alerts" />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
